import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Phase 18: Public Onboarding - Invite Members (Optional)
 * Step 5: Invite family members
 */
function PublicOnboardingInvite() {
  const navigate = useNavigate();
  const [emails, setEmails] = useState(['', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const householdId = sessionStorage.getItem('onboarding_household_id');
    if (!householdId) {
      navigate('/family/onboarding/household');
    }
  }, [navigate]);

  const handleEmailChange = (index, value) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
  };

  const addEmailField = () => {
    setEmails([...emails, '']);
  };

  const removeEmailField = (index) => {
    const newEmails = emails.filter((_, i) => i !== index);
    setEmails(newEmails);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const householdId = sessionStorage.getItem('onboarding_household_id');
      const validEmails = emails.filter(email => email.trim() !== '');

      if (validEmails.length === 0) {
        navigate('/family/onboarding/finish');
        return;
      }

      const response = await axios.post(`${API}/api/onboarding/invite`, {
        household_id: householdId,
        emails: validEmails
      });

      setSuccess(`${response.data.count} Einladung(en) versendet!`);
      setTimeout(() => {
        navigate('/family/onboarding/finish');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Versenden der Einladungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/family/onboarding/finish');
  };

  return (
    <div className="min-h-screen bg-[#050816] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-6 h-6 rounded-full bg-[#22C55E] text-white flex items-center justify-center text-xs">✓</div>
            <div className="w-10 h-1 bg-[#22C55E]"></div>
            <div className="w-6 h-6 rounded-full bg-[#22C55E] text-white flex items-center justify-center text-xs">✓</div>
            <div className="w-10 h-1 bg-[#22C55E]"></div>
            <div className="w-6 h-6 rounded-full bg-[#22C55E] text-white flex items-center justify-center text-xs">✓</div>
            <div className="w-10 h-1 bg-[#22C55E]"></div>
            <div className="w-6 h-6 rounded-full bg-[#22C55E] text-white flex items-center justify-center text-xs">✓</div>
            <div className="w-10 h-1 bg-[#22C55E]"></div>
            <div className="w-6 h-6 rounded-full bg-[#6DCEE8] text-[#050816] flex items-center justify-center font-bold text-xs">5</div>
          </div>
          <p className="text-center text-sm text-neutral-400">Schritt 5 von 5 (Optional)</p>
        </div>

        {/* Card */}
        <div className="bg-neutral-900/80 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-neutral-600/40">
          <h2 className="text-3xl font-bold text-[#F9FAFB] mb-2">Familie einladen</h2>
          <p className="text-[#CBD5F5] mb-6">Laden Sie weitere Familienmitglieder ein</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {emails.map((email, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleEmailChange(index, e.target.value)}
                  placeholder="email@beispiel.de"
                  className="flex-1 px-4 py-3 bg-neutral-800/50 border-2 border-neutral-600/40 text-[#F9FAFB] placeholder-neutral-500 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#5B4B8A]/50 focus:border-[#5B4B8A] transition-all"
                  disabled={loading}
                />
                {emails.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeEmailField(index)}
                    className="px-3 py-3 bg-[#EF4444]/20 text-[#EF4444] rounded-xl hover:bg-[#EF4444]/30 transition-all"
                  >
                    ✖️
                  </button>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={addEmailField}
              className="w-full py-2 text-[#6DCEE8] rounded-xl font-semibold text-sm hover:underline"
            >
              + Weitere E-Mail hinzufügen
            </button>

            {error && (
              <div className="bg-[#EF4444]/10 border border-[#EF4444]/40 text-[#EF4444] px-4 py-3 rounded-xl text-sm">
                <span className="font-semibold">⚠️ {error}</span>
              </div>
            )}

            {success && (
              <div className="bg-[#22C55E]/10 border border-[#22C55E]/40 text-[#22C55E] px-4 py-3 rounded-xl text-sm">
                <span className="font-semibold">✓ {success}</span>
              </div>
            )}

            <div className="space-y-3 pt-2">
              <button
                type="submit"
                className="w-full py-4 bg-gradient-to-r from-[#5B4B8A] to-[#4a3d72] text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-300 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Wird versendet...' : 'Einladungen versenden'}
              </button>
              <button
                type="button"
                onClick={handleSkip}
                className="w-full py-3 text-[#6DCEE8] rounded-xl font-semibold text-sm hover:underline"
              >
                ⏩ Überspringen (später einladen)
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default PublicOnboardingInvite;
